import { DAILY_STATISTICS_OPTIONS, statisticsSession } from '../../api/statisticsQueries';
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Activity, RefreshCw } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { getGaDashboard } from '../../api/gaAdmin';
import { useAuth } from '../../context/AuthContext';
import { useApiEnvironment } from '../../context/ApiEnvironmentContext';
import { t } from '../../utils/i18n';
import { formatPercent, getGaDateRange, getLatestGaRow, GA_RANGE_7D } from '../../utils/gaDashboard';
import DashboardKpi from '../Dashboard/components/DashboardKpi';

export default function AnalyticsSummary({ onError }) {
  const { token } = useAuth();
  const { apiEnvironment } = useApiEnvironment();
  const { from, to } = getGaDateRange(GA_RANGE_7D);
  const query = useQuery({ ...DAILY_STATISTICS_OPTIONS,
    queryKey: ['gaDashboard', apiEnvironment, from, to, statisticsSession(token)],
    queryFn: () => getGaDashboard(from, to, token),
    enabled: !!token,
  });

  React.useEffect(() => {
    if (query.error && onError) onError(query.error);
  }, [query.error, onError]);

  const latest = getLatestGaRow(query.data);
  return (
    <section className="rounded-xl border border-dark-border bg-dark-card p-4 sm:p-5" aria-labelledby="analytics-summary-title">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary" />
            <h2 id="analytics-summary-title" className="text-lg font-bold text-dark-text">
              {t('analytics.summary.title')}
            </h2>
          </div>
          <p className="mt-1 text-xs text-dark-text-secondary">
            {t('analytics.summary.freshness', { date: latest?.date || '—' })}
          </p>
        </div>
        <button type="button" onClick={() => query.refetch()} disabled={query.isFetching} className="inline-flex items-center gap-2 rounded-lg border border-dark-border px-3 py-2 text-sm"><RefreshCw size={14} />{t('dashboard.refresh')}</button>
        <Link
          to="/analytics/overview"
          className="inline-flex items-center gap-2 self-start rounded-lg border border-dark-border px-3 py-2 text-sm font-medium text-dark-text transition-colors hover:border-primary hover:text-primary"
        >
          {t('analytics.summary.open')}
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {query.isLoading ? (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="h-[88px] animate-pulse rounded-lg border border-dark-border bg-dark-bg" />
          ))}
        </div>
      ) : query.error ? (
        <p className="rounded-lg border border-red-900/50 bg-red-950/30 px-4 py-3 text-sm text-red-300">
          {t('dashboard.ga.loadError')}
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <DashboardKpi label={t('dashboard.ga.kpi.dau')} value={latest?.dau ?? '—'} size="primary" />
          <DashboardKpi label={t('analytics.summary.eventsToday')} value={latest?.eventCount ?? '—'} size="primary" />
          <DashboardKpi
            label={t('analytics.crash.crashFreeToday')}
            value={latest ? formatPercent(latest.crashFreeUsersRate) : '—'}
            accent
            size="primary"
          />
          <DashboardKpi
            label={t('analytics.crash.affectedToday')}
            value={latest?.crashAffectedUsers ?? '—'}
            size="primary"
          />
        </div>
      )}
    </section>
  );
}
